import { useCallback, useSyncExternalStore } from 'react';

// Lobby game-tile density. Compact is the shipped default (beta feedback: the
// large tiles show too few games per screen); Large is the roomy artwork-forward
// tile. Persisted per-user so the choice survives reloads.
export type LobbyCardSize = 'compact' | 'large';

const STORAGE_KEY = 'hijack_lobbyCardSize';

const read = (): LobbyCardSize => {
  try {
    // Any stored value other than an explicit 'large' (incl. absent) → compact.
    return localStorage.getItem(STORAGE_KEY) === 'large' ? 'large' : 'compact';
  } catch {
    return 'compact';
  }
};

// Module-level shared store so EVERY consumer stays in sync — the setting lives
// in the Account → Settings panel but the lobby tiles read it, and the two are
// separate component subtrees. A plain per-hook useState wouldn't propagate a
// change from the settings panel to the live lobby. useSyncExternalStore over
// this store fixes that: setLobbyCardSize notifies all mounted subscribers.
let current: LobbyCardSize = read();
const listeners = new Set<() => void>();

const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

const getSnapshot = (): LobbyCardSize => current;

export const setLobbyCardSize = (size: LobbyCardSize): void => {
  if (size === current) return;
  current = size;
  try {
    localStorage.setItem(STORAGE_KEY, size);
  } catch {
    /* localStorage unavailable (private mode) — stays in memory for the session */
  }
  listeners.forEach((l) => l());
};

/**
 * Read + update the shared lobby card-size preference. Reactive across every
 * mounted consumer (settings panel + lobby), so toggling it in Settings resizes
 * the live lobby tiles immediately.
 */
export const useLobbyCardSize = (): {
  cardSize: LobbyCardSize;
  setCardSize: (size: LobbyCardSize) => void;
} => {
  const cardSize = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setCardSize = useCallback((size: LobbyCardSize) => setLobbyCardSize(size), []);
  return { cardSize, setCardSize };
};
